import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getLenis } from '../../hooks/useLenis.js';
import './Navbar.css';

export const Navbar = () => {
  const navRef = useRef(null);
  const drawerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const isDrawerOpenRef = useRef(false);
  isDrawerOpenRef.current = drawerOpen;

  const checkNavTheme = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    if (isDrawerOpenRef.current) {
      nav.dataset.over = '';
      return;
    }

    const stages = Array.from(document.querySelectorAll('.stage'));
    if (!stages.length) {
      nav.dataset.over = '';
      return;
    }

    const edge = nav.getBoundingClientRect().height * 0.75;
    const over = stages.some((stage) => {
      const box = stage.getBoundingClientRect();
      return box.top <= edge && box.bottom > edge;
    });

    nav.dataset.over = over ? 'stage' : '';
  }, []);

  const setOpen = useCallback(
    (open) => {
      const drawer = drawerRef.current;
      setDrawerOpen(open);
      document.body.style.overflow = open ? 'hidden' : '';

      const lenis = getLenis();
      if (open) lenis?.stop();
      else lenis?.start();

      const nav = navRef.current;
      if (nav) {
        if (open) {
          nav.dataset.over = '';
        } else {
          requestAnimationFrame(checkNavTheme);
        }
      }

      if (open && drawer) {
        gsap.from(drawer.querySelectorAll('li'), {
          opacity: 0,
          y: 20,
          duration: 0.5,
          stagger: 0.04,
          delay: 0.2,
          ease: 'power2.out',
        });
      }
    },
    []
  );

  useEffect(() => {
    // /pages/contact.html and /contact are the same page; compare the clean form.
    const clean = (p) => p.replace(/\/index\.html$/, '').replace(/^\/pages\/(.+)\.html$/, '/$1') || '/';
    const here = clean(location.pathname);
    const nav = navRef.current;
    if (!nav) return;

    nav.querySelectorAll('.nav-links a').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (clean(href) === here) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    checkNavTheme();
  }, [location.pathname, checkNavTheme]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    checkNavTheme();

    const trigger = ScrollTrigger.create({
      start: 'top -80',
      end: 99999,
      onUpdate: (self) => {
        nav.dataset.stuck = String(self.scroll() > 80);
        const hide = self.direction === 1 && self.scroll() > 400;
        nav.dataset.hidden = String(hide && !isDrawerOpenRef.current);
        checkNavTheme();
      },
      onRefresh: checkNavTheme,
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isDrawerOpenRef.current) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      trigger.kill();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [checkNavTheme, setOpen]);

  const handleGroupFocusOut = (e) => {
    const group = e.currentTarget;
    if (!group.contains(e.relatedTarget)) {
      const btn = group.querySelector('button');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
  };

  const toggleGroup = (e) => {
    const btn = e.currentTarget;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
  };

  return (
    <>
      <header className="nav" data-nav ref={navRef}>
        <a className="nav-mark" href="/" aria-label="ASTRID FIRST Wealth Advisory">
          <img
            src="/media/logo_black.png"
            alt="ASTRID FIRST Wealth Advisory"
            className="nav-logo nav-logo--dark"
          />
          <img
            src="/media/logo_white.png"
            alt="ASTRID FIRST Wealth Advisory"
            className="nav-logo nav-logo--light"
          />
        </a>

        <nav aria-label="Primary">
          <ul className="nav-links">
            <li>
              <a href="/">Home</a>
            </li>
            <li className="nav-group" onBlur={handleGroupFocusOut}>
              <button
                type="button"
                aria-expanded="false"
                onClick={toggleGroup}
              >
                About us
              </button>
              <ul className="nav-sub">
                <li>
                  <a href="/introduction">
                    <span className="nav-sub-title">Introduction</span>
                    <span className="nav-sub-desc">Who we are, our people and our values</span>
                  </a>
                </li>
                <li>
                  <a href="/business-model">
                    <span className="nav-sub-title">Our Business Model</span>
                    <span className="nav-sub-desc">How you, your bank and ASTRID FIRST work together</span>
                  </a>
                </li>
                <li>
                  <a href="/faq">
                    <span className="nav-sub-title">Frequently Asked Questions</span>
                    <span className="nav-sub-desc">Answers to common questions before you begin</span>
                  </a>
                </li>
              </ul>
            </li>
            <li className="nav-group" onBlur={handleGroupFocusOut}>
              <button
                type="button"
                aria-expanded="false"
                onClick={toggleGroup}
              >
                Wealth management
              </button>
              <ul className="nav-sub">
                <li>
                  <a href="/services">
                    <span className="nav-sub-title">Our Services</span>
                    <span className="nav-sub-desc">Our mandates and specialist planning services</span>
                  </a>
                </li>
                <li>
                  <a href="/approach">
                    <span className="nav-sub-title">Investment Approach</span>
                    <span className="nav-sub-desc">The principles and process behind every portfolio</span>
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <a href="/careers">Careers</a>
            </li>
            <li>
              <a href="/contact">Contact</a>
            </li>
          </ul>
        </nav>

        <div className="nav-actions">
          <a className="nav-phone" href="tel:+6569217700">
            (+65) 6921 7700
          </a>
          <a className="btn" href="/contact">
            Get in touch
          </a>
          <button
            className="menu-toggle"
            type="button"
            data-menu-toggle
            aria-expanded={drawerOpen ? 'true' : 'false'}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen(!drawerOpen)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <div
        className="drawer"
        data-drawer
        data-lenis-prevent
        data-open={drawerOpen ? 'true' : 'false'}
        aria-hidden={drawerOpen ? 'false' : 'true'}
        ref={drawerRef}
      >
        <div className="drawer-panel">
          <p className="drawer-label">Navigation</p>
          <ul className="drawer-links">
            <li>
              <a href="/" onClick={() => setOpen(false)}>
                Home
              </a>
            </li>
            <li>
              <a
                href="/introduction"
                onClick={() => setOpen(false)}
              >
                Introduction
              </a>
            </li>
            <li>
              <a
                href="/business-model"
                onClick={() => setOpen(false)}
              >
                Our Business Model
              </a>
            </li>
            <li>
              <a href="/faq" onClick={() => setOpen(false)}>
                Frequently Asked Questions
              </a>
            </li>
            <li>
              <a
                href="/services"
                onClick={() => setOpen(false)}
              >
                Our Services
              </a>
            </li>
            <li>
              <a
                href="/approach"
                onClick={() => setOpen(false)}
              >
                Investment Approach
              </a>
            </li>
            <li>
              <a
                href="/careers"
                onClick={() => setOpen(false)}
              >
                Careers
              </a>
            </li>
            <li>
              <a
                href="/contact"
                onClick={() => setOpen(false)}
              >
                Contact
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};